import axios from 'axios';
import { requestNotificationPermission, refreshFCMToken, onForegroundMessage } from '@/config/firebase';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOKEN_CACHE_KEY = 'mylocalgo_fcm_token';

// Save FCM token to backend (skips the call when this token is already registered)
const saveTokenToBackend = async (userId, token) => {
  if (localStorage.getItem(TOKEN_CACHE_KEY) === `${userId}:${token}`) {
    return;
  }

  await axios.post(`${API}/notifications/register-token`, {
    user_id: userId,
    fcm_token: token
  });
  localStorage.setItem(TOKEN_CACHE_KEY, `${userId}:${token}`);
};

// Registers a token obtained outside the web SDK (native iOS wrapper)
export const registerNativeToken = async (userId, token) => {
  await saveTokenToBackend(userId, token);
  return token;
};

// Register FCM token with backend (requests permission if needed)
export const registerPushNotifications = async (userId) => {
  if (!userId) return null;
  
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    console.log('Push notifications not supported');
    return null;
  }
  
  try {
    const token = await requestNotificationPermission();
    if (!token) {
      console.log('No FCM token obtained');
      return null;
    }
    
    await saveTokenToBackend(userId, token);
    console.log('Push notification token registered successfully');
    return token;
  } catch (error) {
    console.error('Failed to register push notifications:', error);
    return null;
  }
};

// Silently refresh and re-register the FCM token (for already-granted permissions)
export const refreshAndRegisterToken = async (userId) => {
  if (!userId) return null;
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return null;
  if (Notification.permission !== 'granted') return null;
  
  try {
    const token = await refreshFCMToken();
    if (!token) return null;
    
    await saveTokenToBackend(userId, token);
    console.log('FCM token refreshed and registered');
    return token;
  } catch (error) {
    console.error('Failed to refresh FCM token:', error);
    return null;
  }
};

// Force refresh: delete old token + get new one + register (fixes stale tokens)
export const forceRefreshAndRegister = async (userId) => {
  if (!userId) return null;
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return null;
  
  try {
    // This calls requestNotificationPermission which does deleteToken + getToken
    const token = await requestNotificationPermission();
    if (!token) return null;
    
    await saveTokenToBackend(userId, token);
    console.log('FCM token force-refreshed and registered');
    return token;
  } catch (error) {
    console.error('Failed to force refresh FCM token:', error);
    return null;
  }
};

// Update user location for proximity notifications
export const updateUserLocation = async (userId) => {
  if (!userId || !navigator.geolocation) return;
  
  navigator.geolocation.getCurrentPosition(
    async (position) => {
      try {
        await axios.post(`${API}/notifications/update-location`, {
          user_id: userId,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        console.log('User location updated for notifications');
      } catch (error) {
        console.error('Failed to update location:', error);
      }
    },
    (error) => console.log('Location not available:', error),
    { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
  );
};

// Track user action for interest profiling
export const trackUserAction = async (userId, actionType, category, dealId = null) => {
  if (!userId) return;
  
  try {
    await axios.post(`${API}/notifications/track-action`, {
      user_id: userId,
      action_type: actionType,
      category: category,
      deal_id: dealId
    });
  } catch (error) {
    // Silently fail - tracking shouldn't break UX
  }
};

// Setup foreground notification listener
export const setupForegroundNotifications = (navigate) => {
  return onForegroundMessage((payload) => {
    console.log('Foreground notification:', payload);
    
    const title = payload.notification?.title || payload.data?.title || 'MyLocalGo';
    const body = payload.notification?.body || payload.data?.body || '';
    const clickUrl = payload.data?.click_url;
    
    toast(title, {
      description: body,
      duration: 6000,
      action: clickUrl ? {
        label: 'View',
        onClick: () => navigate(clickUrl)
      } : undefined
    });
  });
};

// Check if notifications are enabled
export const areNotificationsEnabled = () => {
  if (!('Notification' in window)) return false;
  return Notification.permission === 'granted';
};

// Check if should prompt for notifications
export const shouldPromptForNotifications = () => {
  if (!('Notification' in window)) return false;
  return Notification.permission === 'default';
};
